import { apiErrorField, apiErrorMessage } from '@agos/api-client'
import {
  getGetReorderDraftsQueryKey,
  useDeleteReorderDraftsId,
  useGetReorderDrafts,
  useGetReorderDraftsId,
  usePostReorderDraftsAutoGenerate,
  usePostReorderDraftsIdMarkSent,
  usePutReorderDraftsIdLines,
} from '@agos/api-client/inventory'
import { T, formatDateTime, tt, useModalFocus } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FilterBar,
  FilterSelect,
  IconButton,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Eye,
  Mail,
  RefreshCw,
  Send,
  Trash2,
  Truck,
  X,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import {
  byNewest,
  canMail,
  estimateOf,
  filterDrafts,
  formatMoney,
  formatQty,
  lineProblems,
  linesAreEditable,
  linesChanged,
  linesPayload,
  lineValue,
  mailBody,
  mailSubject,
  parseStamp,
  parsedQty,
  sendBlockers,
  statusColor,
  statusLabel,
  withQty,
  withoutLine,
  type DraftDetail,
  type DraftLine,
  type DraftRow,
  type LineProblem,
  type SendBlocker,
} from './reorder-draft'

/**
 * `/reorder-drafts` — purchase-order drafts for what has run low.
 *
 * Auto-generation asks each item's default supplier for the shortfall below its
 * minimum. Legacy lists the drafts, opens one, marks it sent, deletes it, and offers a
 * `mailto:` when the supplier has an address.
 *
 * Two things it does not say, and both drafts in the restored data are examples:
 *
 *  - **FB-082.** The shortfall is asked for in the stock item's own unit, with nothing
 *    converting it into a purchase unit. So the drafts ask LUNYS for **1 g of paprika**
 *    and Bidfood for **4,005 g of liquid cheddar**. Arithmetically right, and not
 *    something a wholesaler delivers.
 *  - **FB-083.** No supplier in the data has an e-mail address, and legacy renders its
 *    mail button only when one exists — so the page's primary action is silently
 *    absent, and a draft that cannot be sent looks like one nobody has got round to.
 *
 * Ported from legacy `pages/reorder-drafts.tsx`.
 */

export function ReorderDraftsPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [status, setStatus] = useState('draft')
  const [openId, setOpenId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState<DraftRow | null>(null)
  const [sending, setSending] = useState<DraftRow | null>(null)
  const [generated, setGenerated] = useState<string | null>(null)

  /* FB-839, §9.553 - **`isError`, because the action this screen offers beside its empty sentence DELETES drafts.**
     `ReorderAutoGenerator` opens with `DELETE FROM reorder_drafts WHERE supplier_id = :supplierId AND status = 'draft'`,
     and Šurany's dump carries two such rows with their lines. So a failed read said *Žiadne návrhy v tomto stave* and put
     *Vygenerovať návrhy* next to it - FB-811's shape, where the read destroys nothing and the blank state invites the
     write that does. */
  const {
    data,
    isLoading,
    isError: draftsFailed,
    refetch: refetchDrafts,
  } = useGetReorderDrafts(status === 'all' ? {} : { status })
  const drafts = useMemo<DraftRow[]>(() => data ?? [], [data])
  const shown = useMemo(() => byNewest(filterDrafts(drafts, status)), [drafts, status])

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetReorderDraftsQueryKey() })
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Operácia sa nepodarila.'),
      variant: 'destructive',
    })

  const generate = usePostReorderDraftsAutoGenerate({
    mutation: {
      onSuccess: (result: unknown) => {
        const body = asRecord(result)
        // §10A.71 — what the run could not draft, and why. A generator that drafted two of
        // ninety-eight shortfalls used to report only the two, so the honest reading of the screen
        // was *this does not work* rather than *these items name no supplier*.
        const skipped = numberOf(body?.skippedNoSupplier)
        setGenerated(
          `${numberOf(body?.generatedDrafts)} ${tt('návrhov pre')} ${numberOf(
            body?.totalSuppliers,
          )} ${tt('dodávateľov')}` +
            (skipped > 0
              ? ` · ${skipped} ${tt('položiek pod minimom nemá dodávateľa - doplňte ho pri položke')}`
              : ''),
        )
        invalidate()
      },
      onError,
    },
  })

  const markSent = usePostReorderDraftsIdMarkSent({
    mutation: {
      onSuccess: () => {
        setSending(null)
        invalidate()
      },
      onError,
    },
  })

  const remove = useDeleteReorderDraftsId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        invalidate()
      },
      onError,
    },
  })

  const columns: Column<DraftRow>[] = useMemo(
    () => [
      {
        key: 'id',
        header: '#',
        sortable: true,
        sortValue: (draft) => draft.id ?? 0,
        render: (draft) => <span className="font-mono text-xs font-bold text-primary">#{draft.id}</span>,
      },
      {
        key: 'supplier',
        header: tt('Dodávateľ'),
        sortable: true,
        sortValue: (draft) => draft.supplierName ?? '',
        render: (draft) => (
          <span className="font-semibold text-sm truncate">{draft.supplierName ?? '-'}</span>
        ),
      },
      {
        key: 'lineCount',
        header: tt('Riadkov'),
        sortable: true,
        sortValue: (draft) => draft.lineCount ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (draft) => (
          <span className="text-xs tabular-nums">{draft.lineCount ?? 0}</span>
        ),
      },
      {
        key: 'estimatedTotal',
        header: tt('Odhad'),
        sortable: true,
        sortValue: (draft) => draft.estimatedTotal ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (draft) =>
          (draft.estimatedTotal ?? 0) > 0 ? (
            <span className="text-xs tabular-nums">{formatMoney(draft.estimatedTotal ?? 0)}</span>
          ) : (
            // A draft worth nothing and one of unknown worth are different things.
            <span className="text-xs text-muted-foreground" title={tt('Žiadny riadok nemá cenu')}>
              -
            </span>
          ),
      },
      {
        key: 'status',
        header: tt('Stav'),
        sortable: true,
        sortValue: (draft) => draft.status ?? '',
        render: (draft) => (
          <StatusBadge label={statusLabel(draft.status)} color={statusColor(draft.status)} />
        ),
      },
      {
        key: 'createdAt',
        header: tt('Vytvorené'),
        sortable: true,
        sortValue: (draft) => parseStamp(draft.createdAt) ?? 0,
        render: (draft) => {
          const createdAt = parseStamp(draft.createdAt)
          return (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {createdAt === null ? '-' : formatDateTime(createdAt)}
            </span>
          )
        },
      },
      {
        key: 'actions',
        header: '',
        render: (draft) => (
          <div className="flex items-center gap-1 justify-end">
            <IconButton
              label={`${tt('Otvoriť návrh')} #${draft.id ?? ''}`}
              onClick={() => setOpenId(draft.id ?? null)}
            >
              <Eye className="w-3.5 h-3.5" />
            </IconButton>
            {draft.status === 'draft' && (
              <IconButton
                label={`${tt('Označiť ako odoslané')} #${draft.id ?? ''}`}
                onClick={() => setSending(draft)}
              >
                <Send className="w-3.5 h-3.5" />
              </IconButton>
            )}
            <IconButton
              label={`${tt('Zmazať návrh')} #${draft.id ?? ''}`}
              tone="red"
              onClick={() => setDeleting(draft)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </IconButton>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Návrhy objednávok')}
        description={tt('Čo sa blíži ku koncu, rozdelené podľa dodávateľa')}
        icon={<Truck className="w-5 h-5" />}
        actions={
          <button
            type="button"
            onClick={() => generate.mutate()}
            /* FB-839, §9.553 - **off while the list is unknown**, which is FB-819's guard applied to a destructive
               action rather than an expensive one. Disabled and not hidden: removing it would read as *this franchise
               cannot generate proposals*, a second false claim, and the banner below says why it is off. */
            disabled={generate.isPending || draftsFailed}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {generate.isPending ? <Spinner className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
            <T>Vygenerovať návrhy</T>
          </button>
        }
      />

      {generated && (
        <p className="text-xs text-emerald-500" data-generate-result>
          <T>Vygenerované:</T> {generated}
        </p>
      )}

      {/* FB-839, §9.553 - before the figures, and the strip lives in the else arm rather than beside the banner:
          §9.552's mutation proved that a notice above three zeros still leaves the three zeros. */}
      {draftsFailed ? (
        <div
          className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 flex items-start gap-2 text-sm"
          data-draft-totals-error
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-destructive" />
          <p className="text-destructive leading-relaxed">
            <T>
              Návrhy sa nepodarilo načítať, takže nevieme, čo je pripravené na objednanie. Generovanie je dočasne
              vypnuté - prepísalo by existujúce návrhy.
            </T>{' '}
            <button
              type="button"
              onClick={() => void refetchDrafts()}
              data-draft-totals-retry
              className="underline font-semibold"
            >
              <T>Skúsiť znova</T>
            </button>
          </p>
        </div>
      ) : (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-draft-totals>
        <StatCard label={tt('Návrhov')} value={String(shown.length)} icon={Truck} color="blue" />
        <StatCard
          label={tt('Riadkov spolu')}
          value={String(shown.reduce((sum, draft) => sum + (draft.lineCount ?? 0), 0))}
          icon={Truck}
          color="violet"
        />
        <StatCard
          label={tt('Odhad spolu')}
          value={
            shown.some((draft) => (draft.estimatedTotal ?? 0) > 0)
              ? formatMoney(shown.reduce((sum, draft) => sum + (draft.estimatedTotal ?? 0), 0))
              : '-'
          }
          icon={Truck}
          color="emerald"
        />
      </div>
      )}

      <FilterBar
        filters={
          <FilterSelect
            value={status}
            label={tt('Stav návrhu')}
            onChange={setStatus}
            options={[
              { value: 'draft', label: tt('Návrhy') },
              { value: 'sent', label: tt('Odoslané') },
              { value: 'cancelled', label: tt('Zrušené') },
              { value: 'all', label: tt('Všetky') },
            ]}
          />
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : shown.length === 0 ? (
        <div className="border border-border rounded-2xl">
          <EmptyState
            icon={<Truck className="w-10 h-10" />}
            description={tt('Žiadne návrhy v tomto stave.')}
          />
        </div>
      ) : (
        <div data-drafts>
          <DataTable<DraftRow>
            columns={columns}
            data={shown}
            rowKey={(draft) => draft.id ?? 0}
            pageSize={25}
            compact
            emptyMessage={tt('Žiadne návrhy')}
          />
        </div>
      )}

      {openId !== null && <DraftDialog draftId={openId} onClose={() => setOpenId(null)} />}

      <ConfirmDialog
        open={sending !== null}
        onClose={() => setSending(null)}
        onConfirm={() => sending?.id !== undefined && markSent.mutate({ id: sending.id })}
        title={`${tt('Označiť návrh')} #${sending?.id ?? ''} ${tt('ako odoslaný?')}`}
        // Marking is bookkeeping: nothing is transmitted. Same distinction as a
        // locally-marked storno on a virtual receipt.
        description={tt(
          'Toto len zapíše, že objednávka odišla - samo sa nič dodávateľovi neposiela.',
        )}
        confirmLabel={tt('Označiť')}
        loading={markSent.isPending}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting?.id !== undefined && remove.mutate({ id: deleting.id })}
        title={`${tt('Zmazať návrh')} #${deleting?.id ?? ''}?`}
        description={tt('Návrh sa dá znovu vygenerovať, kým je položka pod minimom.')}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

function DraftDialog({ draftId, onClose }: { draftId: number; onClose: () => void }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data, isLoading } = useGetReorderDraftsId(draftId)
  const draft = (data ?? {}) as DraftDetail
  const serverLines = useMemo(() => draft.lines ?? [], [draft.lines])

  // §9.77. Edits are held here until *Uložiť*, because `PUT …/lines` is wholesale — it deletes every
  // line and re-inserts what it is sent, so saving per keystroke would rewrite the whole table on
  // each one. It also means closing the panel undoes an accidental keystroke.
  const [edited, setEdited] = useState<DraftLine[] | null>(null)
  const lines = edited ?? serverLines
  const editable = linesAreEditable(draft)
  const changed = edited !== null && linesChanged(edited, serverLines)

  const save = usePutReorderDraftsIdLines({
    mutation: {
      onSuccess: () => {
        setEdited(null)
        void queryClient.invalidateQueries()
      },
      onError: (caught: unknown) =>
        toast({
          variant: 'destructive',
          title: tt('Nepodarilo sa uložiť'),
          description: apiErrorMessage(caught, ''),
        }),
    },
  })

  const estimate = estimateOf(lines)
  const blockers = sendBlockers({ ...draft, lines })
  const mailable = canMail({ ...draft, lines })

  /* §9.494, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. Wrapper is the dismiss scrim, so
     the role stays on it and the trap is bounded by the card inside (§9.469). */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${tt('Návrh objednávky')} #${draftId}`}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-draft-detail
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <p className="font-bold text-sm truncate">
              {tt('Návrh')} #{draftId} · {draft.supplierName ?? ''}
            </p>
            <p className="text-[11px] text-muted-foreground truncate">
              {draft.supplierEmail ? (
                draft.supplierEmail
              ) : (
                <T>Dodávateľ nemá zadaný e-mail</T>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <>
              {/* FB-082 / FB-083 - every reason this cannot go out, named. */}
              {blockers.length > 0 && (
                <div className="space-y-1" data-send-blockers>
                  {blockers.map((blocker) => (
                    <p
                      key={blocker.kind}
                      className="flex items-start gap-1.5 text-xs text-amber-500 leading-relaxed"
                    >
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                      <BlockerText blocker={blocker} />
                    </p>
                  ))}
                </div>
              )}

              <ul className="divide-y divide-border" data-draft-lines>
                {lines.map((line, index) => (
                  <LineRow
                    key={line.id ?? index}
                    line={line}
                    unitLabel={line.unit ?? ''}
                    // §9.77. Editable only on a draft — BACKEND.md §10A.21 refuses the rest, and a
                    // box beside an *Odoslané* badge would be an invitation the server declines.
                    editable={editable}
                    onQty={(qty) => setEdited(withQty(lines, index, qty))}
                    onRemove={() => setEdited(withoutLine(lines, index))}
                  />
                ))}
                {lines.length === 0 && (
                  <li className="py-6 text-sm text-muted-foreground text-center">
                    <T>Návrh nemá žiadne riadky.</T>
                  </li>
                )}
              </ul>

              <div className="flex items-center justify-between text-sm pt-1 border-t border-border">
                <span className="text-muted-foreground">
                  <T>Odhad spolu</T>
                </span>
                <span className="font-bold tabular-nums">
                  {estimate.pricedLines === 0 ? '-' : formatMoney(estimate.totalValue)}
                  {!estimate.complete && estimate.pricedLines > 0 && (
                    <span className="text-[11px] text-muted-foreground font-normal">
                      {' '}
                      ({estimate.pricedLines}/{estimate.totalLines})
                    </span>
                  )}
                </span>
              </div>

              {draft.notes && <p className="text-xs text-muted-foreground">{draft.notes}</p>}
            </>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zavrieť</T>
          </button>
          {/* §9.77. Offered only once something would change, so a panel somebody merely opened does
              not invite a write that rewrites every line to the values it already has. */}
          {editable && changed && (
            <button
              type="button"
              onClick={() => save.mutate({ id: draftId, data: linesPayload(lines) })}
              disabled={save.isPending}
              data-save-lines
              className={cn(BUTTON_PRIMARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
            >
              {save.isPending ? <T>Ukladám…</T> : <T>Uložiť</T>}
            </button>
          )}
          {/* Legacy hides this entirely without an address. Shown and disabled, with
              the reason above, so its absence is not the message. */}
          <a
            href={
              mailable
                ? `mailto:${draft.supplierEmail ?? ''}?subject=${encodeURIComponent(
                    mailSubject(draft),
                  )}&body=${encodeURIComponent(mailBody(draft))}`
                : undefined
            }
            aria-disabled={!mailable}
            className={cn(
              'flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-center transition-opacity flex items-center justify-center gap-2',
              mailable
                ? 'bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity disabled:opacity-50'
                : 'bg-muted text-muted-foreground pointer-events-none opacity-50',
            )}
            data-mail-link
          >
            <Mail className="w-4 h-4" />
            <T>Odoslať e-mailom</T>
          </a>
        </div>
      </div>
    </div>
  )
}

function LineRow({
  line,
  unitLabel,
  editable,
  onQty,
  onRemove,
}: {
  line: DraftLine
  unitLabel: string
  editable: boolean
  onQty: (qty: number) => void
  onRemove: () => void
}) {
  const problems = lineProblems(line)
  const value = lineValue(line)

  return (
    <li className="py-2.5 flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{line.stockItemName ?? '-'}</p>
        <p className="text-[11px] text-muted-foreground font-mono">
          {line.supplierCode ?? tt('bez kódu dodávateľa')}
        </p>
        {problems.map((problem) => (
          <p
            key={problem.kind}
            className="text-[11px] text-amber-500 mt-0.5"
            data-line-problem={problem.kind}
          >
            <LineProblemText problem={problem} />
          </p>
        ))}
      </div>
      <div className="text-right shrink-0">
        {editable ? (
          <div className="flex items-center gap-1.5 justify-end">
            <input
              // `defaultValue`, not `value`: the box holds what somebody is typing, and a partial
              // number — "12," on the way to "12,5" — is not yet a quantity to lift into state.
              // `parsedQty` decides when it is one, and an unreadable box simply does not commit.
              defaultValue={formatQty(line.qty)}
              onChange={(event) => {
                const qty = parsedQty(event.target.value)
                if (qty !== null) onQty(qty)
              }}
              inputMode="decimal"
              aria-label={`${tt('Množstvo')} - ${line.stockItemName ?? ''}`}
              data-line-qty={line.stockItemId}
              className="w-20 px-2 py-1 rounded-lg border border-border bg-card text-sm text-right tabular-nums"
            />
            <span className="text-xs text-muted-foreground w-8 text-left">{unitLabel}</span>
            <IconButton
              label={tt('Odstrániť riadok')}
              tone="red"
              onClick={onRemove}
            >
              <Trash2 className="w-3.5 h-3.5" data-remove-line={line.stockItemId} />
            </IconButton>
          </div>
        ) : (
          <p className="text-sm font-bold tabular-nums whitespace-nowrap">
            {formatQty(line.qty)} {unitLabel}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5">
          {value === null ? '-' : formatMoney(value)}
        </p>
      </div>
    </li>
  )
}

function LineProblemText({ problem }: { problem: LineProblem }) {
  if (problem.kind === 'no-quantity') {
    return <T>Riadok nemá množstvo.</T>
  }
  if (problem.kind === 'no-supplier-code') {
    return <T>Bez kódu dodávateľa - nemusí vedieť, čo od neho chceme.</T>
  }
  return (
    <span>
      {/* FB-082 - `minQty − qty` in the stock unit, with no pack size anywhere. */}
      <T>Toto množstvo nikto nedodáva:</T> {formatQty(problem.qty)} {problem.unit}.{' '}
      <T>Zásoba sa počíta v rovnakej jednotke ako sklad, nie v balení dodávateľa.</T>
    </span>
  )
}

function BlockerText({ blocker }: { blocker: SendBlocker }) {
  if (blocker.kind === 'already-sent') return <T>Tento návrh už bol označený ako odoslaný.</T>
  if (blocker.kind === 'cancelled') return <T>Tento návrh bol zrušený.</T>
  if (blocker.kind === 'no-lines') return <T>Návrh nemá žiadne riadky.</T>
  if (blocker.kind === 'no-email') {
    return (
      <T>
        Dodávateľ nemá zadaný e-mail, takže objednávku nie je kam poslať. Doplňte ho na stránke
        Dodávatelia.
      </T>
    )
  }
  return (
    <span>
      <strong>{blocker.count}</strong>{' '}
      <T>
        riadkov žiada množstvo, ktoré dodávateľ nedodáva. Pred odoslaním ich prepočítajte na jeho
        balenie.
      </T>
    </span>
  )
}


function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function numberOf(value: unknown): number {
  return typeof value === 'number' ? value : 0
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record?.error ?? record?.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string, so every
 * refusal reached the operator as *"HTTP 400 Bad Request: <the real sentence>"*, which is exactly
 * what `apiErrorMessage` exists to avoid.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
